<template>
  <router-view v-if="isStandalonePage" />

  <el-container v-else class="app-container">
    <!-- Sidebar -->
    <el-aside width="220px" class="app-aside">
      <div class="logo">
        <el-icon :size="28">
          <Calendar />
        </el-icon>
        <span>智能教务考务系统</span>
      </div>
      <el-menu :default-active="$route.path" router class="app-menu" background-color="#1e1e2f" text-color="#a0aec0"
        active-text-color="#667eea">
        <el-menu-item index="/semesters">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>学期管理</span>
        </el-menu-item>
        <el-menu-item index="/teachers">
          <el-icon>
            <User />
          </el-icon>
          <span>教师管理</span>
        </el-menu-item>
        <el-menu-item index="/students">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>学生管理</span>
        </el-menu-item>
        <el-menu-item index="/exams">
          <el-icon>
            <Document />
          </el-icon>
          <span>考试管理</span>
        </el-menu-item>
        <el-menu-item index="/statistics">
          <el-icon>
            <DataLine />
          </el-icon>
          <span>工作量统计</span>
        </el-menu-item>
        <el-menu-item index="/grades">
          <el-icon>
            <Reading />
          </el-icon>
          <span>成绩管理</span>
        </el-menu-item>
        <el-menu-item index="/grade-analysis">
          <el-icon>
            <PieChart />
          </el-icon>
          <span>成绩分析</span>
        </el-menu-item>
        <el-menu-item index="/key-class-analysis">
          <el-icon>
            <TrendCharts />
          </el-icon>
          <span>重点班分析</span>
        </el-menu-item>
        <el-menu-item v-if="isCurrentAdmin" index="/users">
          <el-icon>
            <Setting />
          </el-icon>
          <span>用户管理</span>
        </el-menu-item>
        <el-menu-item index="/enroll/reviews">
          <el-icon>
            <Document />
          </el-icon>
          <span>自主招生审核</span>
        </el-menu-item>
      </el-menu>
    </el-aside>

    <!-- Main Content -->
    <el-container>
      <el-header class="app-header">
        <div class="header-content">
          <h2>{{ $route.meta.title || '智能教务考务系统' }}</h2>
          <div class="header-actions">
            <el-tag type="info">{{ currentUser?.username || '未知用户' }}</el-tag>
            <el-tag v-if="isCurrentAdmin" type="danger">管理员</el-tag>
            <el-button link type="primary" @click="handleLogout">退出登录</el-button>
          </div>
        </div>
      </el-header>
      <el-main class="app-main">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </el-main>
    </el-container>

    <!-- Global Scheduling Progress (Non-blocking) -->
    <transition name="el-zoom-in-bottom">
      <div v-if="scheduleStore.generating" class="global-progress-card">
        <div class="progress-header">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>正在自动排程...</span>
        </div>
        <div class="progress-body">
          <p class="step-text">{{ scheduleStore.progressStep }}</p>
          <el-progress :percentage="scheduleStore.progressPercent" :stroke-width="8" />
        </div>
      </div>
    </transition>
  </el-container>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Calendar, User, UserFilled, Document, DataLine, Loading, Reading, TrendCharts, Setting, PieChart } from '@element-plus/icons-vue'
import { useScheduleStore } from '@/stores/schedule'
import { authApi } from '@/api'
import { clearAuth, getUser, isAdmin } from '@/utils/auth'

const scheduleStore = useScheduleStore()
const route = useRoute()
const router = useRouter()

const isStandalonePage = computed(() => !!route.meta.standalone)
const currentUser = ref(getUser())
const isCurrentAdmin = ref(isAdmin())

watch(() => route.path, () => {
  currentUser.value = getUser()
  isCurrentAdmin.value = isAdmin()
})

const handleLogout = async () => {
  try {
    await authApi.logout()
  } catch (error) {
    // Ignore logout API errors and clear local session anyway.
  }
  clearAuth()
  router.replace('/login')
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  height: 100%;
  width: 100%;
}

.app-container {
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
}

.app-aside {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-right: 1px solid #e5e7eb;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 16px;
  color: #4f46e5;
  font-size: 15px;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.app-menu {
  border: none;
  padding: 10px 0;
  background: transparent;
}

.app-menu .el-menu-item {
  margin: 4px 8px;
  border-radius: 8px;
  color: #374151;
}

.app-menu .el-menu-item:hover {
  background: rgba(79, 70, 229, 0.08) !important;
  color: #4f46e5;
}

.app-menu .el-menu-item.is-active {
  background: linear-gradient(90deg, rgba(79, 70, 229, 0.15), transparent) !important;
  border-left: 3px solid #4f46e5;
  color: #4f46e5;
}

.app-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e5e7eb;
  padding: 0 24px;
  height: 64px;
}

.header-content {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-header h2 {
  color: #1f2937;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-main {
  background: transparent;
  padding: 24px;
  overflow-y: auto;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Element Plus overrides - Light Theme */
.el-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.el-card__header {
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
}

.el-table {
  background: transparent;
  --el-table-bg-color: transparent;
  --el-table-header-bg-color: #f9fafb;
  --el-table-tr-bg-color: transparent;
  --el-table-row-hover-bg-color: rgba(79, 70, 229, 0.05);
  --el-table-header-text-color: #6b7280;
  --el-table-text-color: #374151;
  --el-table-border-color: #e5e7eb;
}

.el-button--primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border: none;
}

.el-button--primary:hover {
  background: linear-gradient(135deg, #4338ca 0%, #6d28d9 100%);
}

.el-dialog {
  background: #ffffff;
  border-radius: 16px;
}

.el-dialog__header {
  border-bottom: 1px solid #e5e7eb;
}

.el-dialog__title {
  color: #1f2937;
}

.el-form-item__label {
  color: #374151;
}

.el-input__wrapper,
.el-select__wrapper {
  background: #f9fafb;
  box-shadow: 0 0 0 1px #d1d5db !important;
}

.el-input__wrapper:hover,
.el-select__wrapper:hover {
  box-shadow: 0 0 0 1px #9ca3af !important;
}

.el-input__wrapper:focus-within,
.el-select__wrapper:focus-within {
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3) !important;
}

.el-input__inner {
  color: #1f2937;
}

.el-tag--success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.3);
  color: #059669;
}

.el-tag--info {
  background: rgba(107, 114, 128, 0.1);
  border-color: rgba(107, 114, 128, 0.3);
  color: #4b5563;
}

.el-tag--warning {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: #d97706;
}

.el-tag--danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

/* Global Progress Card */
.global-progress-card {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 10000;
  border: 1px solid #e5e7eb;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #1f2937;
  font-weight: 600;
  font-size: 14px;
}

.progress-body .step-text {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}
</style>
